// src/components/landing/Pricing.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  // billingCycle is now always 'monthly' (yearly option removed)
  const billingCycle = 'monthly';
  const [plansData, setPlansData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const paymentService = (await import('../../../services/paymentService')).default;
        const res = await paymentService.getPlans();
        if (res.status === 'success' && Array.isArray(res.data)) {
          // Group plans by plan type and interval
          const groupedPlans = {};
          res.data.forEach(plan => {
            const key = plan.plan;
            if (!groupedPlans[key]) {
              groupedPlans[key] = {};
            }
            groupedPlans[key][plan.interval] = plan;
          });

          // Convert to frontend format (monthly only)
          const frontendPlans = Object.keys(groupedPlans).map(planKey => {
            const intervals = groupedPlans[planKey];
            const monthly = intervals.monthly;

            // Define plan details based on plan type
            const planDetails = getPlanDetails(planKey, monthly);

            return {
              ...planDetails,
              price: {
                monthly: monthly?.formatted || '$0'
              }
            };
          });

          setPlansData(frontendPlans);
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        // Fallback to static plans if API fails
        setPlansData(getStaticPlans());
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const getPlanDetails = (planKey, monthly) => {
    const planConfigs = {
      free: {
        name: 'Free',
        tagline: 'Perfect for getting started',
        features: [
          'Up to 50 notes',
          '5 AI searches per month',
          'Basic flashcard generation',
          'Standard search',
          'Community support',
        ],
        limitations: ['Limited AI features', 'Ad-supported'],
        cta: 'Get Started Free',
        popular: false,
        highlight: false,
      },
      pro: {
        name: 'Pro',
        tagline: 'Most popular for power users',
        features: [
          'Unlimited notes',
          'Unlimited AI searches',
          'Advanced flashcard generation',
          'Advanced search with filters',
          'No ads',
          'Zoom meeting integration',
          'Priority support',
          'Export to multiple formats',
          'Custom templates',
        ],
        limitations: [],
        cta: 'Start Free Trial',
        popular: true,
        highlight: true,
      },
      team: {
        name: 'Team',
        tagline: 'For teams and organizations',
        features: [
          'Everything in Pro, plus:',
          'Up to 10 team members',
          'Shared workspaces',
          'Team analytics',
          'Admin controls',
          'SAML SSO',
          'Custom branding',
          'Dedicated account manager',
        ],
        limitations: [],
        cta: 'Contact Sales',
        popular: false,
        highlight: false,
      }
    };

    return planConfigs[planKey] || planConfigs.free;
  };

  const getStaticPlans = () => [
    {
      name: 'Free',
      tagline: 'Perfect for getting started',
      price: { monthly: '$0' },
      features: [
        'Up to 50 notes',
        '5 AI searches per month',
        'Basic flashcard generation',
        'Standard search',
        'Community support',
      ],
      limitations: ['Limited AI features', 'Ad-supported'],
      cta: 'Get Started Free',
      popular: false,
      highlight: false,
    },
    {
      name: 'Pro',
      tagline: 'Most popular for power users',
      price: { monthly: '$9.99' },
      features: [
        'Unlimited notes',
        'Unlimited AI searches',
        'Advanced flashcard generation',
        'Advanced search with filters',
        'No ads',
        'Zoom meeting integration',
        'Priority support',
        'Export to multiple formats',
        'Custom templates',
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: true,
      highlight: true,
    },
    {
      name: 'Team',
      tagline: 'For teams and organizations',
      price: { monthly: '$29.99' },
      features: [
        'Everything in Pro, plus:',
        'Up to 10 team members',
        'Shared workspaces',
        'Team analytics',
        'Admin controls',
        'SAML SSO',
        'Custom branding',
        'Dedicated account manager',
      ],
      limitations: [],
      cta: 'Contact Sales',
      popular: false,
      highlight: false,
    },
  ];

  const savingsForPlan = (planName) => {
    // No savings messaging since yearly option is removed
    return '';
  };

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Choose the perfect plan for your needs. All plans include core features.
          </p>
        </div>

        {/* Billing info - Monthly only */}
        <div className="flex justify-center mb-12">
          <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">Billed monthly</p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plansData.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-3xl border-2 transition-all duration-300 transform hover:scale-[1.02] ${
                plan.highlight
                  ? 'border-blue-500 dark:border-blue-400 shadow-2xl'
                  : 'border-gray-200 dark:border-gray-700'
              } ${plan.highlight ? 'bg-white dark:bg-gray-800' : 'bg-white dark:bg-gray-800'}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">{plan.tagline}</p>
                </div>

                {/* Price */}
                <div className="text-center mb-8">
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-5xl font-bold text-gray-900 dark:text-white">
                      {plan.price.monthly}
                    </span>
                    {plan.name !== 'Free' && (
                      <span className="text-gray-600 dark:text-gray-400 ml-2">/month</span>
                    )}
                  </div>
                  {(savingsForPlan(plan.name) && plan.name !== 'Free') && (
                    <p className="text-green-600 dark:text-green-400 font-medium">
                      {savingsForPlan(plan.name)}
                    </p>
                  )}
                </div>

                {/* Features */}
                <div className="mb-8">
                  <ul className="space-y-4">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.limitations.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Limitations:</p>
                      <ul className="space-y-2">
                        {plan.limitations.map((limitation, idx) => (
                          <li key={idx} className="flex items-center text-sm">
                            <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span className="text-gray-600 dark:text-gray-400">{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <Link
                  to={plan.name === 'Free' ? '/signup' : plan.name === 'Team' ? '/contact' : '/signup'}
                  className={`block w-full text-center py-4 rounded-xl font-bold transition ${
                    plan.highlight
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Guarantee */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 px-6 py-4 rounded-2xl">
            <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="font-bold">14-day money-back guarantee</span>
            <span className="mx-3">•</span>
            <span>No credit card required for free plan</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;